import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, Users } from "lucide-react";
import { getObraStats } from "@/services/obraStats";

// Linha de exibição derivada do workforce do RDO mais recente da obra
interface CollaboratorRow {
  id: string;
  name: string;
  function: string;
  quantity: number;
  sector: string;
}

interface WorkforceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Obra cujo último RDO alimenta a lista */
  obraId?: string;
}

export function WorkforceModal({ open, onOpenChange, obraId }: WorkforceModalProps) {
  const [searchTerm, setSearchTerm] = useState("");

  // Mão de obra real: workforce do último RDO da obra
  const [collaborators, setCollaborators] = useState<CollaboratorRow[]>([]);
  const [lastDate, setLastDate] = useState<string | null>(null);
  useEffect(() => {
    if (!open || !obraId) return;
    let active = true;
    getObraStats(obraId)
      .then((s) => {
        if (!active) return;
        const last = s.rdos[0];
        setLastDate(last?.date ?? null);
        setCollaborators(
          (last?.workforce ?? []).map((w, i) => ({
            id: String(i),
            name: w.name || "—",
            function: w.function || "—",
            quantity: w.quantity ?? 1,
            sector: w.team || w.assignedTeam || (w.type === "terceirizada" ? "Terceirizada" : "Direta"),
          }))
        );
      })
      .catch((e) => console.error("WorkforceModal:", e));
    return () => { active = false; };
  }, [open, obraId]);

  // Filtrar colaboradores por busca
  const filteredCollaborators = collaborators.filter(collaborator =>
    collaborator.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    collaborator.function.toLowerCase().includes(searchTerm.toLowerCase()) ||
    collaborator.sector.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calcular total de colaboradores
  const totalCollaborators = collaborators.reduce((sum, collaborator) => sum + collaborator.quantity, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[95vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Mão de Obra - Total: {totalCollaborators} colaboradores
            {lastDate && (
              <span className="text-sm font-normal text-muted-foreground">
                (último RDO: {lastDate.split("-").reverse().join("/")})
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Filtro de Busca */}
        <div className="pb-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Buscar por nome, função ou setor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Lista de Colaboradores */}
        <div className="flex-1 overflow-y-auto space-y-2">
          {filteredCollaborators.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>Nenhum colaborador encontrado.</p>
            </div>
          ) : (
            filteredCollaborators.map((collaborator) => (
              <Card key={collaborator.id} className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm">
                      <span className="font-medium text-foreground w-32">{collaborator.name}</span>
                      <span className="text-foreground font-medium w-32">{collaborator.function}</span>
                      <Badge variant="outline" className="text-xs">
                        {collaborator.sector}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Qtd:</span>
                      <Badge variant="default" className="text-xs font-semibold">
                        {collaborator.quantity}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Rodapé com estatísticas */}
        <div className="pt-4 border-t">
          <div className="flex justify-between items-center text-sm text-muted-foreground">
            <p>Exibindo {filteredCollaborators.length} funções</p>
            <p>
              Total de colaboradores: {filteredCollaborators.reduce((sum, collaborator) => sum + collaborator.quantity, 0)}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}